import type { ErrorEvent } from '@sentry/node';

// These predicates run in beforeSend, in the browser, BEFORE Sentry's server-side symbolication:
// frame filenames are raw script URLs (our chunks under /_next/static/, third-party script URLs,
// or — for code a webview host injects into the page — the document URL itself). The
// webpack://_N_E/… shapes shown in the Sentry UI never reach this code.

// Sentry's inbound filter tests these against the event message and against the value of the
// last exception, alone and as `Type: value`: a string matches as a substring, a RegExp as is.
// Each one comes from code we do not control: a browser, an extension or a vendor script.
export const IGNORED_ERRORS: (string | RegExp)[] = [
  // How @sentry/browser names a rejection whose reason is not an Error: a primitive ("Non-Error
  // promise rejection captured with value: …"), a plain object ("Object captured as promise
  // rejection with keys: …") or an event ("Event `CustomEvent` (type=unhandledrejection) captured
  // as promise rejection"). No stack, nothing to act on: the ones seen come from extensions.
  'Non-Error promise rejection captured',
  'captured as promise rejection',
  // A Safari extension messaging a tab that is gone. No stack.
  'Invalid call to runtime.sendMessage(). Tab not found.',
  // DuckDuckGo's iOS browser messaging its own native layer. No stack.
  'WKWebView API client did not respond to this postMessage',
  // Scripts Android WebViews inject under <anonymous>: an autofill script (execute_auto_fill)
  // reading a global its host never defined, and SnapTube's bridge to its Java side.
  'xbrowser is not defined',
  'Error invoking jsReceiveMessages',
  // The wallet provider Brave injects at the top of the document on iOS.
  /window\.ethereum/,
  // An adware extension whose content script fetches its own host.
  /Failed to fetch \(safesearchinc\.com\)/,
  // The reCAPTCHA script rejecting a promise of its own; the execute() calls of the apply forms
  // are caught where they are made.
  /^reCAPTCHA Timeout/,
];

const THIRD_PARTY_ANALYTICS_FILENAME_FRAGMENTS = [
  'fbevents.js',
  'connect.facebook.net',
  '/signals/config/',
  'gtag/js',
  'googletagmanager.com',
  'google-analytics.com',
];

// The Meta (Facebook) Pixel and Google gtag scripts (loaded via Segment) throw opaque,
// message-less errors from their own event-handler callbacks — Firefox-only, 0 user impact
// (Sentry JOBS-2KE / WEB-7677). They reach us only because @sentry/browser's default
// browserApiErrorsIntegration wraps addEventListener. We can't change third-party code, so we
// drop events whose stack references such a script. No first-party guard is possible here: these
// stacks always contain the SDK's own wrapper frame, which is bundled into our chunks and
// therefore indistinguishable from application code in raw frames.
export const isThirdPartyAnalyticsNoise = (event: ErrorEvent) => {
  const filenames = (event.exception?.values ?? [])
    .flatMap(value => value.stacktrace?.frames ?? [])
    .map(frame => frame.filename ?? '');

  return filenames.some(filename =>
    THIRD_PARTY_ANALYTICS_FILENAME_FRAGMENTS.some(fragment => filename.includes(fragment)),
  );
};

const IN_APP_BROWSER_BRIDGE_FUNCTIONS = [
  'sendDataToNative',
  'sendPageHideMessage',
  'sendBeforeUnloadMessage',
  'sendJsBlockingTimeMessage',
  'setupIosCallbackHandler',
];

const IN_APP_BROWSER_BRIDGE_MESSAGE_FRAGMENTS = ['window.webkit.messageHandlers', 'SCDynimacBridge'];

// Meta's Instagram/Facebook in-app browsers inject a bridge script into every page they render.
// On iOS its pagehide listener dereferences window.webkit.messageHandlers after the app has
// unregistered its native handlers at webview teardown (Sentry JOBS-3DD family); on Android the
// same script reports through a dead JavascriptInterface under its iabjs:// pseudo-scheme
// (JOBS-2N1). Snapchat's iOS in-app browser injects a script referencing its SCDynimacBridge
// global, which the app does not always define. window.onerror attributes injected code to the
// document URL, so there is no third-party filename to match: we match the bridge's function
// names, its iabjs:// scheme, and the bridge globals the messages name (which also covers events
// where Meta minifies the function names; no jobs code touches window.webkit or SCDynimacBridge).
export const isInAppBrowserBridgeNoise = (event: ErrorEvent) => {
  const values = event.exception?.values ?? [];

  const namesBridgeGlobal = values.some(value =>
    IN_APP_BROWSER_BRIDGE_MESSAGE_FRAGMENTS.some(fragment => (value.value ?? '').includes(fragment)),
  );

  const hasInjectedBridgeFrame = values
    .flatMap(value => value.stacktrace?.frames ?? [])
    .some(
      frame =>
        IN_APP_BROWSER_BRIDGE_FUNCTIONS.includes(frame.function ?? '') || (frame.filename ?? '').startsWith('iabjs://'),
    );

  return namesBridgeGlobal || hasInjectedBridgeFrame;
};

// Chrome on iOS, and the Google app's own browser.
const GOOGLE_APP_USER_AGENT_TOKENS = ['CriOS', 'GSA/'];
const BUNDLE_PATH = '/_next/';

// Google's apps run their own scripts inside the page, such as the automatic translation of a page
// written in another language than the device's. Their failures reach the global handlers with no
// frame from our bundle: attributed to the document URL, to no file at all, or with no stack. Code
// of ours always leaves a frame under /_next/, which keeps its errors reported in these apps.
// The user agent comes from the header the browser SDK copies onto the event before beforeSend.
export const isGoogleAppInjectedScriptNoise = (event: ErrorEvent) => {
  const userAgent = event.request?.headers?.['User-Agent'] ?? '';
  const isGoogleApp = GOOGLE_APP_USER_AGENT_TOKENS.some(token => userAgent.includes(token));
  const values = event.exception?.values ?? [];
  const hasBundleFrame = values
    .flatMap(value => value.stacktrace?.frames ?? [])
    .some(frame => frame.filename?.includes(BUNDLE_PATH));

  return isGoogleApp && values.length > 0 && !hasBundleFrame;
};
